import store from "@/store";
import { Action, config, getModule, Module, VuexModule } from "vuex-module-decorators";
import Viewpoints from "@/store/modules/Viewpoints";
import Modelling from "@/store/modules/Modelling";
import { FactorGroup, FactorResource } from "@/api/model/FactorResource";
import { ViewpointFactorResource } from "@/api/model/ViewpointFactorResource";

config.rawError = true

const name = 'weighting';
if (module.hot) {
    if (store.hasModule(name)) {
        store.unregisterModule(name);
    }
}

@Module({dynamic: true, store: store, name: name, namespaced: true})
export default class Weighting extends VuexModule {


    private viewpointWeightChangedViewpointFactors: { [viewpointId: string]: { [id: string]: boolean } } = {};

    get viewpointGroupDots() {
        let selectedViewpoints = getModule(Viewpoints).selectedViewpoints
        if (!selectedViewpoints) return {};
        let factorResources = getModule(Modelling).publishedModelFactorResources;
        let viewpointFactorResources = getModule(Viewpoints).viewpointFactorResources;
        let viewpointsWeightingMap: { [viewpointId: string]: { [id: string]: number } } = {};
        for (let viewpoint of selectedViewpoints) {
            let viewpointFactors = viewpointFactorResources[viewpoint.viewpointId];
            if (factorResources && viewpointFactors) {
                let viewpointWeightingMap = {};
                Weighting.getDotsForTreeElement('M', factorResources, viewpointFactors, viewpointWeightingMap);
                viewpointsWeightingMap[viewpoint.viewpointId] = viewpointWeightingMap;
            }
        }
        return viewpointsWeightingMap;

    }

    @Action
    async updateViewpointWeightsWithoutSubmit(payload: { viewpointId: string, factors: { [id: string]: number } }) {
        let viewpointFactorResources = getModule(Viewpoints).viewpointFactorResources[payload.viewpointId];
        for (let fId of Object.keys(payload.factors)) {
            let numWeightingDots = payload.factors[fId];
            let viewpointFactor = viewpointFactorResources[fId];
            if (!viewpointFactor) {
                let newVpFactor: ViewpointFactorResource = {
                    id: fId, numWeightingDots: numWeightingDots, viewpointId: payload.viewpointId
                }
                getModule(Viewpoints).setViewpointFactorResource(newVpFactor);
                if (!this.viewpointWeightChangedViewpointFactors[payload.viewpointId]) {
                    this.viewpointWeightChangedViewpointFactors[payload.viewpointId] = {};
                }
                this.viewpointWeightChangedViewpointFactors[payload.viewpointId][fId] = true;
            } else if (viewpointFactor && viewpointFactor.numWeightingDots !== numWeightingDots) {
                viewpointFactor.numWeightingDots = numWeightingDots;
                getModule(Viewpoints).setViewpointFactorResource(viewpointFactor);
                if (!this.viewpointWeightChangedViewpointFactors[payload.viewpointId]) {
                    this.viewpointWeightChangedViewpointFactors[payload.viewpointId] = {};
                }
                this.viewpointWeightChangedViewpointFactors[payload.viewpointId][fId] = true;
            }
        }
    }

    @Action
    async submitWeightChanges(viewpointId: string) {
        let viewpointFactorResourcesMap = getModule(Viewpoints).viewpointFactorResources
        let viewpointFactorResources = viewpointFactorResourcesMap[viewpointId];
        let updatedViewpointFactorResources = Object.keys(this.viewpointWeightChangedViewpointFactors[viewpointId])
            .map(id => viewpointFactorResources[id])
            .filter(vf => vf);
        delete this.viewpointWeightChangedViewpointFactors[viewpointId];
        await getModule(Viewpoints).putViewpointFactors({
            viewpointId: viewpointId,
            viewpointFactors: updatedViewpointFactorResources
        })

    }

    private static getDotsForTreeElement(factorResourceId: string, factorResources: { [id: string]: FactorResource }, viewpointFactors: any, resultingResourcesDots: { [id: string]: number }): number {
        let factorResource = factorResources[factorResourceId];
        if (!factorResource) return 0
        if (factorResource.type === 'FACTOR') {
            let dots;
            if (viewpointFactors && viewpointFactors[factorResourceId] && viewpointFactors[factorResourceId].numWeightingDots != null) {
                dots = viewpointFactors[factorResourceId].numWeightingDots;
            } else {
                dots = factorResource.numWeightingDots || 0;
            }
            resultingResourcesDots[factorResourceId] = dots;
            return dots
        }
        let factorGroup = factorResource as FactorGroup;
        if (factorGroup.groupTypeDefinition && factorGroup.groupTypeDefinition.type === 'TABLE_FIXED') {
            let dots = Weighting.getDotsForTable(factorGroup, factorResources, viewpointFactors, resultingResourcesDots)
            resultingResourcesDots[factorResourceId] = dots;
            return dots;
        }

        //January 17 Scoring Update - When group uses min or max summary, use the highest child factor weight
        let groupDots = (factorGroup.childrenIds || [])
            .map(child => Weighting.getDotsForTreeElement(child, factorResources, viewpointFactors, resultingResourcesDots))
            .reduce((a, b) => {
                if (viewpointFactors && viewpointFactors[factorResourceId] && ["MAX", "MIN"].includes(viewpointFactors[factorResourceId].summaryScoringFunction)) {
                    return Math.max(a, b);
                } else if (viewpointFactors && viewpointFactors[factorResourceId] && viewpointFactors[factorResourceId].summaryScoringFunction === 'WEIGHTED_SUM') {
                    return (a || 0) + (b || 0);
                } else if (["MAX", "MIN"].includes(factorResource.summaryScoringFunction)) {
                    return Math.max(a, b)
                }
                else
                    return (a || 0) + (b || 0)
            }, 0);

        resultingResourcesDots[factorResourceId] = groupDots;
        return groupDots;
    }

    private static getDotsForTable(tableFactorGroup: FactorGroup, factorResources: { [p: string]: FactorResource }, viewpointFactors: any, resultingResourcesDots: { [p: string]: number }): number {

        let rows = tableFactorGroup.childrenIds.map(id => factorResources[id] as FactorGroup).filter(f => f && f.groupTypeDefinition && f.groupTypeDefinition.type === 'TABLE_ROW')
        let columns = tableFactorGroup.childrenIds.map(id => factorResources[id] as FactorGroup).filter(f => f && f.groupTypeDefinition && f.groupTypeDefinition.type === 'TABLE_COLUMN')

        let tableFactorIds = columns.map(gc => gc.childrenIds).flat()

        for (let row of rows) {
            let rowDots = row.childrenIds
                .map(fId => {
                    let dots;
                    if (viewpointFactors && viewpointFactors[fId] && viewpointFactors[fId].numWeightingDots != null) {
                        dots = viewpointFactors[fId].numWeightingDots;
                    } else {
                        dots = factorResources[fId].numWeightingDots || 1;
                    }
                    resultingResourcesDots[fId] = dots;
                    return dots
                })
                .reduce((a, b) => a + b, 0)
            resultingResourcesDots[row.id] = rowDots;
        }
        for (let col of columns) {
            let colDots = col.childrenIds
                .map(id => resultingResourcesDots[id])
                .reduce((a, b) => (a || 0) + (b || 0), 0)

            resultingResourcesDots[col.id] = colDots;
        }

        return tableFactorIds.map(id => resultingResourcesDots[id])
            .reduce((a, b) => (a || 0) + (b || 0), 0)
    }

}